import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, MailCheck, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BrandMark } from "@/components/AppShell";
import { roleHome } from "@/components/ProtectedRoute";

export default function VerifyEmail() {
  const { verifyEmail, resendOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const stateEmail = location?.state?.email || "";
  const stateOtp = location?.state?.dev_otp || "";
  const stateEmailSent = location?.state?.email_sent || false;

  const [email, setEmail] = useState(stateEmail);
  const [code, setCode] = useState(stateOtp);
  const [devOtp, setDevOtp] = useState(stateOtp);
  const [emailSent, setEmailSent] = useState(stateEmailSent);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (stateOtp) {
      toast.info(`Your verification code: ${stateOtp}`, { duration: 10000 });
    } else if (stateEmailSent) {
      toast.success("Verification code sent to your inbox — check your email.");
    }
  }, [stateOtp, stateEmailSent]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await verifyEmail(email, code);
      toast.success(`Email verified — welcome, ${user.name || user.email}`);
      navigate(roleHome(user.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    if (!email) {
      toast.error("Please enter your email first.");
      return;
    }
    setResending(true);
    try {
      const res = await resendOtp(email);
      if (res?.email_sent) {
        setDevOtp("");
        setEmailSent(true);
        toast.success("New code sent to your inbox.");
      } else if (res?.dev_otp) {
        setDevOtp(res.dev_otp);
        setCode(res.dev_otp);
        setEmailSent(false);
        toast.success(`New code: ${res.dev_otp}`);
      } else {
        toast.success("New code issued — please check your email.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-25" />
        <div className="relative h-full p-12 flex flex-col justify-between text-primary-foreground">
          <Link to="/"><BrandMark /></Link>
          <div>
            <div className="overline text-primary-foreground/70">Verified logins</div>
            <h2 className="mt-3 font-serif text-4xl leading-tight">
              Every account, verified.<br /> Every purpose, consented.
            </h2>
            <p className="mt-4 text-primary-foreground/80 max-w-md">
              An email OTP is the first step in our two-tier verification — professional credentials
              are separately reviewed by our admin team.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 text-xs text-primary-foreground/70">
            <ShieldCheck size={14} /> DPDP-compliant · Consent audit-trailed
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/"><BrandMark /></Link></div>
          <div className="overline">Verify email</div>
          <h1 className="mt-2 font-serif text-4xl text-primary">Enter your 6-digit code.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We&apos;ve issued a one-time code valid for 15 minutes.
          </p>

          {emailSent && !devOtp && (
            <div className="mt-6 rounded-md border border-pastel-mint-border bg-pastel-mint-bg p-4 text-sm" data-testid="email-sent-panel">
              <div className="overline text-[10px] text-pastel-mint-fg">Email sent</div>
              <div className="mt-1 text-pastel-mint-fg/95">
                A verification email has been sent to <strong>{email}</strong>. Check your inbox (and spam) for a 6-digit code.
              </div>
            </div>
          )}

          {devOtp && (
            <div className="mt-6 rounded-md border border-pastel-mint-border bg-pastel-mint-bg p-4 text-sm" data-testid="dev-otp-panel">
              <div className="overline text-[10px] text-pastel-mint-fg">Your verification code</div>
              <div className="mt-1 text-pastel-mint-fg/95">
                We&apos;ve issued your one-time code below (also sent to <strong>{email}</strong> where possible). Click <em>Verify</em> to continue.
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="font-mono text-2xl text-primary tracking-widest" data-testid="dev-otp-value">{devOtp}</div>
                <button
                  type="button"
                  data-testid="dev-otp-copy"
                  onClick={() => {
                    navigator.clipboard?.writeText(devOtp);
                    toast.success("Code copied to clipboard");
                  }}
                  className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-white border border-pastel-mint-border text-pastel-mint-fg hover:bg-pastel-mint-bg/60"
                >
                  Copy
                </button>
              </div>
              <div className="mt-2 text-[11px] text-pastel-mint-fg/70">
                Didn&apos;t receive the email? Use the code above to continue.
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="verify-email-form">
            <div className="space-y-2">
              <Label htmlFor="v-email">Email</Label>
              <Input
                id="v-email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="verify-email-input" placeholder="you@example.in"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="v-code">OTP</Label>
              <Input
                id="v-code" inputMode="numeric" pattern="\d{6}" maxLength={6} required
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="verify-code-input"
                className="font-mono tracking-[0.5em] text-center text-xl"
                placeholder="••••••"
              />
            </div>
            <Button type="submit" className="w-full h-11" disabled={submitting || code.length !== 6} data-testid="verify-submit-button">
              {submitting ? "Verifying…" : (<><MailCheck size={16} className="mr-2" /> Verify &amp; sign in <ArrowRight size={16} className="ml-2" /></>)}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={onResend}
              disabled={resending}
              className="text-primary font-medium hover:underline inline-flex items-center gap-1.5"
              data-testid="verify-resend-button"
            >
              <RefreshCw size={13} /> {resending ? "Sending…" : "Resend code"}
            </button>
            <Link to="/login" className="text-muted-foreground hover:text-primary" data-testid="verify-back-to-login">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
